import React, { useState, useRef, useEffect } from 'react'
import { Play, Pause, ExternalLink, ChevronUp, ChevronDown, Music, X, Volume2, VolumeX, RotateCcw, RotateCw } from 'lucide-react'
import { usePodcastContext } from '../contexts/PodcastContext'
import { DataService } from '../services/DataService'
import { formatTimestamp } from '../utils/textUtils'

const AudioWidget = () => {
  const { currentTrack, setCurrentTrack, isAudioWidgetExpanded, setIsAudioWidgetExpanded } = usePodcastContext()
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(1)
  const [showVolumeSlider, setShowVolumeSlider] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [volumeBeforeMute, setVolumeBeforeMute] = useState(1)
  const [volumePopupPosition, setVolumePopupPosition] = useState({ bottom: 120, left: 180 })
  const audioRef = useRef(null)
  const volumeTimeoutRef = useRef(null)
  const volumeControlRef = useRef(null)

  // Debug pour voir si le widget reçoit les données
  console.log('🎧 AudioWidget rendu avec currentTrack:', currentTrack)

  // Charger l'audio quand la piste change
  useEffect(() => {
    if (currentTrack && audioRef.current) {
      const audioUrl = DataService.getAudioUrl(currentTrack.episodeId, currentTrack.timestamp)
      console.log('🎵 URL audio chargée:', audioUrl)
      
      if (audioUrl) {
        audioRef.current.src = audioUrl
        audioRef.current.load()
        
        // Démarrer automatiquement la lecture après chargement
        const handleCanPlay = () => {
          // Si on a un timestamp, chercher à ce moment-là
          if (currentTrack.timestamp) {
            const timeInSeconds = DataService.timestampToSeconds(currentTrack.timestamp)
            audioRef.current.currentTime = timeInSeconds
          }
          
          // Démarrer automatiquement la lecture
          audioRef.current.play().then(() => {
            console.log('🎵 Lecture automatique démarrée')
            setIsPlaying(true)
          }).catch(error => {
            console.log('❌ Erreur lecture auto:', error)
            // La lecture automatique peut être bloquée par le navigateur
          })
          
          // Retirer l'event listener après utilisation
          audioRef.current.removeEventListener('canplay', handleCanPlay)
        }
        
        audioRef.current.addEventListener('canplay', handleCanPlay)
      }
    }
    // Reset du state quand pas de track
    if (!currentTrack) {
      setIsPlaying(false)
      setCurrentTime(0)
      setDuration(0)
      setIsAudioWidgetExpanded(false)
    }
  }, [currentTrack])

  // Nettoyer les timeouts au démontage
  useEffect(() => {
    return () => {
      if (volumeTimeoutRef.current) {
        clearTimeout(volumeTimeoutRef.current)
      }
    }
  }, [])

  // Gérer les clics extérieurs pour cacher le slider
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showVolumeSlider) {
        // Vérifier si le clic est sur le bouton volume ou le pop-up
        const isClickOnVolumeButton = volumeControlRef.current && volumeControlRef.current.contains(event.target)
        const isClickOnVolumePopup = event.target.closest('[data-volume-popup]')
        
        if (!isClickOnVolumeButton && !isClickOnVolumePopup) {
          hideVolumeSlider()
        }
      }
    }

    if (showVolumeSlider) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showVolumeSlider])

  // Gestionnaires audio
  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause()
      } else {
        audioRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime)
    }
  }

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration)
    }
  }

  const handleSeek = (e) => {
    if (audioRef.current) {
      const seekTime = (e.target.value / 100) * duration
      audioRef.current.currentTime = seekTime
      setCurrentTime(seekTime)
    }
  }

  const handleVolumeChange = (e) => {
    const newVolume = e.target.value / 100
    setVolume(newVolume)
    
    // Si on était muté et qu'on change le volume, démuter automatiquement
    if (isMuted && newVolume > 0) {
      setIsMuted(false)
    }
    
    if (audioRef.current) {
      audioRef.current.volume = newVolume
    }
    
    // Réinitialiser le timeout de cache automatique
    resetVolumeTimeout()
  }

  const handleVolumeClick = () => {
    if (!showVolumeSlider) {
      // État 1 → État 2 : Afficher le slider
      // Calculer la position du pop-up basée sur le bouton volume
      if (volumeControlRef.current) {
        const rect = volumeControlRef.current.getBoundingClientRect()
        const windowHeight = window.innerHeight
        const windowWidth = window.innerWidth
        
        // S'assurer que le pop-up ne sort pas de l'écran
        const popupHeight = 120 // Hauteur approximative du pop-up
        const popupWidth = 40   // Largeur réduite du pop-up
        
        // Centrer le pop-up horizontalement par rapport au bouton
        const buttonCenterX = rect.left + rect.width / 2
        const popupLeft = buttonCenterX - popupWidth / 2
        
        setVolumePopupPosition({
          bottom: Math.min(windowHeight - rect.top + 10, windowHeight - popupHeight - 20),
          left: Math.max(10, Math.min(popupLeft, windowWidth - popupWidth - 10)) // Empêcher le débordement
        })
      }
      setShowVolumeSlider(true)
      resetVolumeTimeout()
    } else {
      // État 2 : Gérer mute/unmute
      if (isMuted) {
        // Démuter
        setIsMuted(false)
        setVolume(volumeBeforeMute)
        if (audioRef.current) {
          audioRef.current.volume = volumeBeforeMute
        }
      } else {
        // Muter
        setIsMuted(true)
        setVolumeBeforeMute(volume)
        setVolume(0)
        if (audioRef.current) {
          audioRef.current.volume = 0
        }
      }
      resetVolumeTimeout()
    }
  }

  const hideVolumeSlider = () => {
    setShowVolumeSlider(false)
    if (volumeTimeoutRef.current) {
      clearTimeout(volumeTimeoutRef.current)
      volumeTimeoutRef.current = null
    }
  }

  const resetVolumeTimeout = () => {
    if (volumeTimeoutRef.current) {
      clearTimeout(volumeTimeoutRef.current)
    }
    
    volumeTimeoutRef.current = setTimeout(() => {
      hideVolumeSlider()
    }, 15000) // 15 secondes
  }

  const handleClose = () => {
    if (audioRef.current) {
      audioRef.current.pause()
    }
    setCurrentTrack(null)
    setIsAudioWidgetExpanded(false)
    setIsPlaying(false)
  }

  const handleOpenSpotify = () => {
    if (currentTrack.spotifyUrl) {
      window.open(currentTrack.spotifyUrl, '_blank')
    }
  }

  const formatTime = (time) => {
    if (isNaN(time)) return '00:00:00'
    const hours = Math.floor(time / 3600)
    const minutes = Math.floor((time % 3600) / 60)
    const seconds = Math.floor(time % 60)
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }

  const skipBack = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 15)
    }
  }

  const skipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 15)
    }
  }

  // Retourner null si pas de piste courante - APRÈS tous les hooks
  if (!currentTrack) {
    console.log('❌ Pas de currentTrack, widget masqué')
    return null
  }

  console.log('✅ Widget affiché avec:', currentTrack)

  return (
    <>
      {/* Styles CSS pour les sliders */}
      <style>{`
        .custom-progress-slider::-webkit-slider-thumb {
          appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #8b5cf6;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(139, 92, 246, 0.3);
          transition: all 0.2s ease;
        }
        .custom-progress-slider::-webkit-slider-thumb:hover {
          background: #a78bfa;
          box-shadow: 0 4px 12px rgba(139, 92, 246, 0.5);
          transform: scale(1.1);
        }
        .custom-progress-slider::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #8b5cf6;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 8px rgba(139, 92, 246, 0.3);
          transition: all 0.2s ease;
        }
        .custom-progress-slider::-moz-range-thumb:hover {
          background: #a78bfa;
          box-shadow: 0 4px 12px rgba(139, 92, 246, 0.5);
          transform: scale(1.1);
        }
        
        .custom-volume-slider {
          writing-mode: bt-lr !important; /* IE */
          -webkit-appearance: slider-vertical !important; /* WebKit */
          width: 4px !important;
          height: 64px !important;
          background: #8b5cf6 !important;
          outline: none !important;
          border-radius: 2px !important;
          cursor: pointer !important;
          border: none !important;
        }
        
        .custom-volume-slider::-webkit-slider-track {
          width: 4px !important;
          height: 64px !important;
          background: #8b5cf6 !important;
          border-radius: 2px !important;
          border: none !important;
          -webkit-appearance: none !important;
        }
        
        .custom-volume-slider::-webkit-slider-thumb {
          appearance: none !important;
          -webkit-appearance: none !important;
          width: 12px !important;
          height: 12px !important;
          border-radius: 50% !important;
          background: #8b5cf6 !important;
          cursor: pointer !important;
          box-shadow: 0 2px 6px rgba(139, 92, 246, 0.4) !important;
          transition: all 0.2s ease !important;
          border: none !important;
        }
        
        .custom-volume-slider::-webkit-slider-thumb:hover {
          background: #a78bfa !important;
          box-shadow: 0 3px 10px rgba(139, 92, 246, 0.6) !important;
          transform: scale(1.15) !important;
        }
        
        .custom-volume-slider::-moz-range-thumb {
          width: 12px !important;
          height: 12px !important;
          border-radius: 50% !important;
          background: #8b5cf6 !important;
          cursor: pointer !important;
          border: none !important;
          box-shadow: 0 2px 6px rgba(139, 92, 246, 0.4) !important;
          transition: all 0.2s ease !important;
        }
        
        .custom-volume-slider::-moz-range-thumb:hover {
          background: #a78bfa !important;
          box-shadow: 0 3px 10px rgba(139, 92, 246, 0.6) !important;
          transform: scale(1.15) !important;
        }
        
        .custom-volume-slider::-moz-range-track {
          width: 4px !important;
          height: 64px !important;
          background: #8b5cf6 !important;
          border-radius: 2px !important;
          border: none !important;
        }
      `}</style>
      
      <div 
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'rgba(30, 27, 75, 0.95)',
          backdropFilter: 'blur(15px)',
          borderTop: '1px solid rgba(167, 139, 250, 0.2)',
          zIndex: 9999,
          transition: 'all 0.3s ease',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.4)'
        }}
      >
      {/* Élément audio HTML5 */}
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        preload="metadata"
      />

      {/* Version compacte */}
      <div className="max-w-[1200px] mx-auto px-4">
        <div className="flex items-center justify-between" style={{ paddingTop: '1.5rem', paddingBottom: '1rem' }}>
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="min-w-0 flex-1">
              <h4 className="font-semibold text-text-primary truncate text-base" style={{ marginBottom: 0 }}>
                {currentTrack.title}
              </h4>
              {currentTrack.timestamp && (
                <p className="text-text-muted text-sm truncate">
                  Démarré à {formatTimestamp(currentTrack.timestamp)}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Boutons de lecture */}
            <button
              onClick={skipBack}
              className="btn btn-ghost p-2"
              aria-label="Reculer 15s"
            >
              <RotateCcw size={18} />
            </button>

            <button
              onClick={handlePlayPause}
              className="btn btn-primary p-3 rounded-full"
              aria-label={isPlaying ? 'Pause' : 'Lecture'}
            >
              {isPlaying ? <Pause size={20} /> : <Play size={20} />}
            </button>

            <button
              onClick={skipForward}
              className="btn btn-ghost p-2"
              aria-label="Avancer 15s"
            >
              <RotateCw size={18} />
            </button>

            {/* Contrôle de volume avec système à deux états */}
            <div ref={volumeControlRef} className="relative flex items-center px-2">
              <button
                onClick={handleVolumeClick}
                className="btn btn-ghost p-2"
                aria-label="Contrôle du volume"
              >
                {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
            </div>

            {/* Boutons d'interface */}
            <button
              onClick={() => setIsAudioWidgetExpanded(!isAudioWidgetExpanded)}
              className="btn btn-ghost p-2"
              aria-label={isAudioWidgetExpanded ? 'Réduire' : 'Développer'}
            >
              {isAudioWidgetExpanded ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </button>


          </div>
        </div>

        {/* Barre de progression (toujours visible) */}
        <div className="pb-2">
          <div className="flex items-center gap-3">
            <span className="text-xs text-text-muted min-w-0">
              {formatTime(currentTime)}
            </span>
                        <input
              type="range"
              min="0"
              max="100"
              value={duration ? (currentTime / duration) * 100 : 0}
              onChange={handleSeek}
              className="flex-1 appearance-none cursor-pointer custom-progress-slider"
              style={{
                height: '6px',
                borderRadius: '3px',
                background: `linear-gradient(to right, #8b5cf6 0%, #8b5cf6 ${duration ? (currentTime / duration) * 100 : 0}%, rgba(167, 139, 250, 0.3) ${duration ? (currentTime / duration) * 100 : 0}%, rgba(167, 139, 250, 0.3) 100%)`,
                outline: 'none',
                WebkitAppearance: 'none',
                margin: '0 10px'
              }}
            />
            <span className="text-xs text-text-muted min-w-0">
              {formatTime(duration)}
            </span>
          </div>
        </div>

        {/* Version étendue */}
        {isAudioWidgetExpanded && (
          <div className="pb-6 border-t border-border-light pt-6 animate-slide-up">
            <div className="flex justify-center">
              {/* Contrôles audio avancés */}
              <div className="bg-background-light rounded-xl p-4 border border-border-light max-w-xs">
                <div className="space-y-4">
                  <button
                    onClick={handleOpenSpotify}
                    className="btn btn-secondary w-full flex items-center justify-center gap-2"
                  >
                    <ExternalLink size={18} />
                    Ouvrir sur Spotify
                  </button>
                  
                  <button
                    onClick={handleClose}
                    className="btn btn-ghost w-full flex items-center justify-center gap-2"
                  >
                    <X size={18} />
                    Fermer le lecteur
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pop-up slider de volume en position fixe */}
      {showVolumeSlider && (
        <div 
          data-volume-popup="true"
          style={{
            position: 'fixed',
            bottom: `${volumePopupPosition.bottom}px`,
            left: `${volumePopupPosition.left}px`,
            background: 'rgba(30, 27, 75, 0.95)',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(167, 139, 250, 0.3)',
            borderRadius: '8px',
            padding: '12px 8px',
            zIndex: 10000,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '6px',
            transition: 'all 0.2s ease',
            animation: 'fadeInScale 0.2s ease-out',
            minWidth: '40px',
            width: '40px'
          }}
        >
          <Volume2 size={12} className="text-text-muted" />
          <input
            type="range"
            min="0"
            max="100"
            value={isMuted ? 0 : volume * 100}
            onChange={handleVolumeChange}
            className="custom-volume-slider"
            orient="vertical"
          />
          <span className="text-xs text-text-muted" style={{ fontSize: '10px' }}>
            {isMuted ? '0' : `${Math.round(volume * 100)}`}
          </span>
        </div>
      )}
    </div>
    </>
  )
}

export default AudioWidget 